import { subscriptionManager } from './subscription.js';
import { clearAllGardenElements } from './garden-elements.js';
import { clearAllLayouts } from './garden-layout-image.js';

const ONBOARDING_DISMISSED_KEY = 'np_onboarding_dismissed_v1';
const ONBOARDING_COMPLETED_KEY = 'np_onboarding_completed_v1';
/** Panel hidden for the current browser tab/session only; next visit opens again */
const SESSION_INSTRUCTIONS_PANEL_CLOSED_KEY = 'np_onboarding_instructions_closed_session_v1';
const ONBOARDING_JOURNAL_GOTO_KEY = 'np_onboarding_journal_goto_v1';
const ONBOARDING_JOURNAL_SAMPLE_OPENED_KEY = 'np_onboarding_journal_sample_opened_v1';
const ONBOARDING_EXPORTED_KEY = 'np_onboarding_exported_v1';

let containerEl = null;
let tooltipEl = null;
let activeTooltipItemId = null;
let observersConnected = false;
let instructionsBtnEl = null;

const checklistItems = [
    {
        id: 'name-bed',
        label: 'Name your garden bed',
        selector: '#garden-name',
        tip: 'Type a short bed name so saves and exports are easy to identify.',
        isDone: () => {
            const input = document.getElementById('garden-name');
            return !!input?.value?.trim();
        }
    },
    {
        id: 'set-bed-size',
        label: 'Set your bed size',
        selector: '#length',
        tip: 'Set Length and Width, then click "Change Garden Size" to rebuild the grid.',
        isDone: () => {
            const lengthInput = document.getElementById('length');
            const widthInput = document.getElementById('width');
            if (!lengthInput || !widthInput) return false;
            return String(lengthInput.value) !== '8' || String(widthInput.value) !== '4';
        }
    },
    {
        id: 'place-first-plant',
        label: 'Place your first plant',
        selector: '#grid',
        tip: 'Click any plant button, then click a grid square to place it.',
        isDone: () => !!document.querySelector('.grid-item-5x5 .plant-label')
    },
    {
        id: 'pick-zone',
        label: 'Select your climate zone',
        selector: '#zone-select',
        tip: 'Pick your zone to highlight plants that are a better fit.',
        isDone: () => {
            const select = document.getElementById('zone-select');
            return !!select?.value;
        }
    },
    {
        id: 'send-layout',
        label: 'Send bed to layout',
        selector: '#capture-garden-image',
        tip: 'Click "Send Bed to Layout" to add this bed to your full garden plan area.',
        isDone: () => {
            try {
                const raw = localStorage.getItem('garden_image_layouts');
                if (!raw) return false;
                const arr = JSON.parse(raw);
                return Array.isArray(arr) && arr.length > 0;
            } catch {
                return false;
            }
        }
    },
    {
        id: 'goto-journal',
        label: 'Go to Journal',
        selector: '#view-journal-btn',
        tip: 'Click "View the Journal". Non-premium users see a read-only preview (sample journal data).',
        isDone: () => localStorage.getItem(ONBOARDING_JOURNAL_GOTO_KEY) === '1'
    },
    {
        id: 'view-sample-journal-entries',
        label: 'View sample journal entries',
        selector: '#view-journal-btn',
        tip: 'On the journal preview page, find "Sample Summer Kitchen Garden" and click its "Garden Journal" button to view sample entries.',
        isDone: () => localStorage.getItem(ONBOARDING_JOURNAL_SAMPLE_OPENED_KEY) === '1'
    },
    {
        id: 'save-or-export',
        label: 'Download your plan',
        selector: '#download-pdf-bed',
        tip: 'Non-premium can still export: use "Download PDF" (bed summary) or "Download This Plan" (full layout image).',
        isDone: () =>
            localStorage.getItem(ONBOARDING_EXPORTED_KEY) === '1' ||
            localStorage.getItem('np_onboarding_saved_or_exported_v1') === '1'
    }
];

function setFinishedKey(key) {
    localStorage.setItem(key, '1');
}

function getProgress() {
    const doneCount = checklistItems.filter(item => item.isDone()).length;
    return {
        doneCount,
        total: checklistItems.length,
        isComplete: doneCount === checklistItems.length
    };
}

function removeTooltip() {
    if (tooltipEl?.parentNode) tooltipEl.remove();
    tooltipEl = null;
    activeTooltipItemId = null;

    document.querySelectorAll('.onboarding-target-highlight').forEach(el => {
        el.classList.remove('onboarding-target-highlight');
    });
}

function positionTooltip(target) {
    if (!tooltipEl || !target) return;
    const rect = target.getBoundingClientRect();
    const top = window.scrollY + rect.bottom + 10;
    const left = window.scrollX + rect.left + Math.max(0, rect.width / 2 - 170);
    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.left = `${Math.max(12, left)}px`;
}

function openTooltip(itemId) {
    removeTooltip();
    const item = checklistItems.find(x => x.id === itemId);
    if (!item) return;

    const target = document.querySelector(item.selector);
    if (!target) return;

    activeTooltipItemId = itemId;
    target.classList.add('onboarding-target-highlight');

    tooltipEl = document.createElement('div');
    tooltipEl.className = 'onboarding-tooltip';
    tooltipEl.innerHTML = `
        <div class="onboarding-tooltip-title">${item.label}</div>
        <div class="onboarding-tooltip-body">${item.tip}</div>
        <button type="button" class="onboarding-tooltip-close">Got it</button>
    `;
    document.body.appendChild(tooltipEl);
    positionTooltip(target);

    const closeBtn = tooltipEl.querySelector('.onboarding-tooltip-close');
    closeBtn?.addEventListener('click', removeTooltip);
}

function renderChecklist() {
    if (!containerEl) return;
    const progress = getProgress();
    const itemsHtml = checklistItems.map(item => {
        const done = item.isDone();
        return `
            <li class="onboarding-item ${done ? 'done' : ''}">
                <span class="status">${done ? '✓' : '○'}</span>
                <span class="text">${item.label}</span>
                ${done ? '' : `<button type="button" class="help" data-item-id="${item.id}">Show me</button>`}
            </li>
        `;
    }).join('');

    const starsHtml = checklistItems
        .map((_, index) => {
            const filled = index < progress.doneCount;
            const cls = filled ? 'fa fa-star onboarding-star onboarding-star--filled' : 'fa fa-star-o onboarding-star onboarding-star--empty';
            return `<span class="onboarding-star-wrap" aria-hidden="true"><i class="${cls}"></i></span>`;
        })
        .join('');

    containerEl.innerHTML = `
        <div class="onboarding-header">
            <strong>Getting Started</strong>
            <div class="onboarding-header-actions">
                <button type="button" class="reset" aria-label="Reset onboarding">Reset</button>
                <button type="button" class="dismiss" aria-label="Dismiss onboarding">×</button>
            </div>
        </div>
        <div class="onboarding-sub">
            <div class="onboarding-progress-text">Complete ${progress.doneCount}/${progress.total} steps</div>
            <div class="onboarding-stars" role="img" aria-label="${progress.doneCount} of ${progress.total} steps completed">
                ${starsHtml}
            </div>
        </div>
        <ul class="onboarding-list">${itemsHtml}</ul>
    `;

    containerEl.querySelectorAll('.help').forEach(btn => {
        btn.addEventListener('click', () => {
            const itemId = btn.getAttribute('data-item-id');
            if (itemId) openTooltip(itemId);
        });
    });

    containerEl.querySelector('.dismiss')?.addEventListener('click', () => {
        // Hide until next page visit; do not persist dismiss across sessions.
        try {
            sessionStorage.setItem(SESSION_INSTRUCTIONS_PANEL_CLOSED_KEY, '1');
        } catch {
            /* ignore */
        }
        teardown();
        syncInstructionsButtonVisibility();
    });

    containerEl.querySelector('.reset')?.addEventListener('click', () => {
        try {
            localStorage.removeItem(ONBOARDING_DISMISSED_KEY);
            localStorage.removeItem(ONBOARDING_COMPLETED_KEY);
            localStorage.removeItem(ONBOARDING_JOURNAL_GOTO_KEY);
            localStorage.removeItem(ONBOARDING_JOURNAL_SAMPLE_OPENED_KEY);
            localStorage.removeItem(ONBOARDING_EXPORTED_KEY);
            localStorage.removeItem('np_onboarding_saved_or_exported_v1');

            // Reset planner UI fields so the checklist truly starts over.
            const nameInput = document.getElementById('garden-name');
            if (nameInput) nameInput.value = '';

            const lengthInput = document.getElementById('length');
            const widthInput = document.getElementById('width');
            if (lengthInput) lengthInput.value = '8';
            if (widthInput) widthInput.value = '4';

            // Clear current bed grid (also clears garden name in updateGridSize()).
            if (typeof window.updateGrid === 'function') {
                window.updateGrid(false);
            }

            // Clear zone highlights/selection.
            const zoneSelect = document.getElementById('zone-select');
            const clearZoneBtn = document.getElementById('clear-zone');
            if (zoneSelect) {
                zoneSelect.value = '';
            }
            // Clear button also removes highlights and hides the message.
            clearZoneBtn?.click?.();

            // Reset layout canvas saved snapshots and placed elements.
            clearAllLayouts?.();
            clearAllGardenElements?.();

            // Clear plant selection highlight (Escape clears selection).
            document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
        } finally {
            try {
                sessionStorage.removeItem(SESSION_INSTRUCTIONS_PANEL_CLOSED_KEY);
            } catch {
                /* ignore */
            }
            // Reopen the onboarding checklist UI after reset.
            teardown();
            setTimeout(evaluateVisibility, 50);
        }
    });

    if (progress.isComplete) {
        // Keep checklist visible so users can re-read steps; they close with × if desired.
        setFinishedKey(ONBOARDING_COMPLETED_KEY);
    }
}

function markExportAction() {
    localStorage.setItem(ONBOARDING_EXPORTED_KEY, '1');
    renderChecklist();
}

function markJournalGotoAction() {
    localStorage.setItem(ONBOARDING_JOURNAL_GOTO_KEY, '1');
    renderChecklist();
}

function connectObservers() {
    if (observersConnected) return;
    observersConnected = true;

    document.getElementById('garden-name')?.addEventListener('input', renderChecklist);
    document.getElementById('length')?.addEventListener('change', renderChecklist);
    document.getElementById('width')?.addEventListener('change', renderChecklist);
    document.getElementById('zone-select')?.addEventListener('change', renderChecklist);

    document.getElementById('capture-garden-image')?.addEventListener('click', () => {
        setTimeout(renderChecklist, 200);
    });

    document.getElementById('view-journal-btn')?.addEventListener('click', markJournalGotoAction);

    ['download-pdf-bed', 'download-btn', 'download-garden-plan'].forEach(id => {
        document.getElementById(id)?.addEventListener('click', markExportAction);
    });

    const grid = document.getElementById('grid');
    if (grid) {
        const observer = new MutationObserver(() => renderChecklist());
        observer.observe(grid, { childList: true, subtree: true });
    }

    window.addEventListener('scroll', () => {
        if (!activeTooltipItemId) return;
        const item = checklistItems.find(x => x.id === activeTooltipItemId);
        const target = item ? document.querySelector(item.selector) : null;
        if (target) positionTooltip(target);
    }, { passive: true });

    window.addEventListener('resize', () => {
        if (!activeTooltipItemId) return;
        const item = checklistItems.find(x => x.id === activeTooltipItemId);
        const target = item ? document.querySelector(item.selector) : null;
        if (target) positionTooltip(target);
    });
}

function teardown() {
    removeTooltip();
    if (containerEl?.parentNode) containerEl.remove();
    containerEl = null;
    if (instructionsBtnEl) {
        instructionsBtnEl.style.bottom = '16px';
    }
}

function shouldShowForUser() {
    const info = subscriptionManager.getSubscriptionInfo?.() ?? {};
    return info.isPremium === false;
}

function createInstructionsButton() {
    if (instructionsBtnEl) return;
    instructionsBtnEl = document.createElement('button');
    instructionsBtnEl.type = 'button';
    instructionsBtnEl.id = 'onboarding-instructions-btn';
    instructionsBtnEl.className = 'onboarding-instructions-btn';
    instructionsBtnEl.innerHTML = '<i class="fa fa-book" aria-hidden="true" style="margin-right:6px;"></i>Instructions';
    instructionsBtnEl.addEventListener('click', () => {
        // Reopen the checklist for this session.
        try {
            sessionStorage.removeItem(SESSION_INSTRUCTIONS_PANEL_CLOSED_KEY);
        } catch {
            /* ignore */
        }
        localStorage.removeItem(ONBOARDING_DISMISSED_KEY);
        localStorage.removeItem(ONBOARDING_COMPLETED_KEY);
        // Keep progress keys (journal/export) intact so the checklist shows accurate done-state.
        teardown();
        // Re-run the normal show flow so `containerEl` is recreated.
        setTimeout(() => {
            evaluateVisibility();
            syncInstructionsButtonVisibility();
        }, 0);
    });
    document.body.appendChild(instructionsBtnEl);
}

function syncInstructionsButtonVisibility() {
    if (!instructionsBtnEl) return;
    if (!shouldShowForUser()) {
        instructionsBtnEl.style.display = 'none';
        return;
    }
    // Keep the button visible all the time for non-premium users.
    instructionsBtnEl.style.display = 'flex';
    updateInstructionsButtonPosition();
}

function updateInstructionsButtonPosition() {
    // If the checklist is open, lift the Instructions button above it to prevent overlap.
    if (containerEl && containerEl.parentNode) {
        const rect = containerEl.getBoundingClientRect();
        // rect.top is distance from top of viewport to the checklist.
        // We want the button to sit above the checklist with a small gap.
        const gapPx = 10;
        const desiredBottom = Math.max(16, Math.floor(window.innerHeight - rect.top + gapPx));
        // Avoid pushing it off-screen if the checklist is very high.
        const maxBottom = Math.max(16, Math.floor(window.innerHeight - 40));
        instructionsBtnEl.style.bottom = `${Math.min(desiredBottom, maxBottom)}px`;
        return;
    }

    instructionsBtnEl.style.bottom = '16px';
}

function createContainer() {
    if (containerEl) return;
    containerEl = document.createElement('aside');
    containerEl.className = 'onboarding-checklist';
    document.body.appendChild(containerEl);
}

function evaluateVisibility() {
    if (!shouldShowForUser()) {
        teardown();
        syncInstructionsButtonVisibility();
        return;
    }

    let closedThisSession = false;
    try {
        closedThisSession = sessionStorage.getItem(SESSION_INSTRUCTIONS_PANEL_CLOSED_KEY) === '1';
    } catch {
        closedThisSession = false;
    }
    if (closedThisSession) {
        teardown();
        syncInstructionsButtonVisibility();
        return;
    }

    createContainer();
    connectObservers();
    renderChecklist();
    syncInstructionsButtonVisibility();
}

export function initializeNonPremiumOnboarding() {
    subscriptionManager.onSubscriptionChange(() => {
        evaluateVisibility();
        syncInstructionsButtonVisibility();
    });

    createInstructionsButton();
    setTimeout(evaluateVisibility, 300);

    window.addEventListener('resize', () => {
        syncInstructionsButtonVisibility();
    });
}

// Debug hook for quick retesting.
// Usage: open browser console and run `resetNonPremiumOnboarding()`
window.resetNonPremiumOnboarding = () => {
    try {
        sessionStorage.removeItem(SESSION_INSTRUCTIONS_PANEL_CLOSED_KEY);
        localStorage.removeItem(ONBOARDING_DISMISSED_KEY);
        localStorage.removeItem(ONBOARDING_COMPLETED_KEY);
        localStorage.removeItem(ONBOARDING_JOURNAL_GOTO_KEY);
        localStorage.removeItem(ONBOARDING_JOURNAL_SAMPLE_OPENED_KEY);
        localStorage.removeItem(ONBOARDING_EXPORTED_KEY);
        localStorage.removeItem('np_onboarding_saved_or_exported_v1');
    } finally {
        teardown();
        setTimeout(evaluateVisibility, 50);
    }
};
